import BaseListStore from 'stores/BaseListStore';
import FlagStore, { IFlag } from 'stores/flag/FlagStore';
import { SortDirection } from 'constants/SortDirection';
import { IDataFetchOptions } from '../../types';
import log from 'services/helpers/log';
import { destroy, get, getPage, patch, post } from 'stores/_services/dataService';
import { observable, makeObservable } from 'mobx';

const NO_FLAG = {
    id: -1,
    name: 'No flag',
    color: 'no'
};

export default class FlagListStore extends BaseListStore<FlagStore> {
    menuFlagIds: string[] = [];

    constructor() {
        super({
            itemStore: FlagStore,
            sortBy: 'weight',
            sortDirection: SortDirection.DESC
        });

        makeObservable(this, {
            menuFlagIds: observable
        });
    }

    get menuFlags(): IFlag[] {
        return [NO_FLAG, ...this.menuFlagIds.map((id) => this.data.get(id)?.flag)];
    }

    get flags(): IFlag[] {
        return [...this.menuFlagIds.map((id) => this.data.get(id)?.flag)];
    }

    async getMenuFlags(): Promise<void> {
        const options = {
            filter: {
                order: 'weight ASC'
            }
        };

        const { data } = await get('flags', options);

        this._updateData(data);
        this._updateIds(data, 0, this.menuFlagIds);
    }

    async getFlags(args: IDataFetchOptions = {}): Promise<void> {
        const shouldReset = this._updateListState(args);

        const options = {
            count: !this.count || shouldReset,
            filter: {
                limit: this.limit,
                skip: this.skip,
                order: [this.sort, 'weight ASC']
            }
        };

        const { data } = await getPage('flags', options);

        if (shouldReset) {
            this.clearIds();
        }

        this._updateData(data);
        this._updateIds(data, this.skip);
        this._updateCount(data);
    }

    async saveFlag(flag: IFlag): Promise<void> {
        const flagDto = {
            name: flag.name,
            color: flag.color,
            weight: flag.weight
        };

        if (flag.id) {
            const res = await patch(`flags/${flag.id}`, flagDto);
            this._updateData(res);
        } else {
            await post('flags', flagDto);
            this.getFlags({ reset: true });
        }
    }

    async deleteFlag(flagId: string): Promise<void> {
        if (!flagId) {
            log.error('FlagId is required for deletion');
        }

        await destroy(`flags/${flagId}`);

        this.clearItem(flagId);
        this.menuFlagIds = this.menuFlagIds.filter((id) => id !== flagId);
    }
}
